import React from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  ShieldCheck,
  CreditCard,
  Globe2,
  Scale,
  LineChart,
  Wallet,
  CheckCircle2,
  Lock,
  Sparkles,
  Quote,
} from "lucide-react";
import Counter from "../components/Counter";

const IMG = {
  hero: "https://images.pexels.com/photos/7821906/pexels-photo-7821906.jpeg",
  about1: "https://images.unsplash.com/photo-1563013544-824ae1b704d3?w=800",
  about2: "https://images.unsplash.com/photo-1726065235158-d9c3f817f331?w=800",
  about3: "https://images.unsplash.com/photo-1603201667141-5a2d4c673378?w=800",
  leading: "https://images.unsplash.com/photo-1556761175-b413da4baf72?w=1200",
  empower: "https://images.unsplash.com/photo-1568992687947-868a62a9f521?w=1200",
  t1: "https://images.unsplash.com/photo-1629425733761-caae3b5f2e50?w=200",
  t2: "https://images.unsplash.com/photo-1573497019940-1c28c88b4f3e?w=200",
  t3: "https://images.pexels.com/photos/30004323/pexels-photo-30004323.jpeg?w=200",
  blog1: "https://images.unsplash.com/photo-1726607424623-6d9fee974241?w=800",
  blog2: "https://images.pexels.com/photos/2988232/pexels-photo-2988232.jpeg?w=800",
  blog3: "https://images.unsplash.com/photo-1683313107043-283d0319a11e?w=800",
};

export default function HomePage() {
  return (
    <>
      <Hero />
      <TrustBar />
      <About />
      <Payment />
      <Solutions />
      <Leading />
      <WhyChooseUs />
      <Empower />
      <Testimonials />
      <Blog />
      <CTA />
    </>
  );
}

function Hero() {
  return (
    <section className="relative overflow-hidden bg-ink-900 text-white">
      <div
        className="absolute inset-0 bg-cover bg-center opacity-40"
        style={{ backgroundImage: `url(${IMG.hero})` }}
        aria-hidden
      />
      <div className="absolute inset-0 bg-hero-gradient" aria-hidden />
      <div
        className="absolute inset-0 bg-grid-dark opacity-40"
        style={{ backgroundSize: "24px 24px" }}
        aria-hidden
      />

      <div className="container-page relative z-10 grid items-center gap-14 py-24 sm:py-32 lg:grid-cols-2 lg:py-40">
        <div className="fade-in">
          <span className="inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/5 px-3 py-1 text-xs font-medium uppercase tracking-[0.22em] text-brand-300 backdrop-blur-sm">
            <Sparkles className="h-3.5 w-3.5" />
            Trusted by high-risk merchants
          </span>
          <h1 className="mt-6 font-display text-4xl font-bold leading-[1.05] sm:text-5xl lg:text-6xl">
            High-Risk Merchant
            <br />
            <span className="bg-gradient-to-r from-brand-300 via-brand-200 to-white bg-clip-text text-transparent">
              Accounts &amp; Processing
            </span>
          </h1>
          <p className="mt-6 max-w-xl text-base leading-relaxed text-white/75 sm:text-lg">
            Secure, accessible payment solutions and card processing for high-risk industries.
          </p>
          <div className="mt-8 flex flex-wrap items-center gap-3">
            <Link to="/contact-us" className="btn-primary">
              Get Started Now
              <ArrowRight className="h-4 w-4" />
            </Link>
            <Link to="/about-us" className="btn-ghost">
              Learn More
            </Link>
          </div>

          <div className="mt-12 grid max-w-md grid-cols-3 gap-6 border-t border-white/10 pt-8">
            <HeroStat value="99.9%" label="Uptime" />
            <HeroStat value="PCI L1" label="Certified" />
            <HeroStat value="24/7" label="Support" />
          </div>
        </div>

        <div className="relative hidden lg:block">
          <div className="relative mx-auto w-full max-w-md">
            <div className="absolute -inset-12 rounded-full bg-brand-500/20 blur-3xl" />
            <div className="relative overflow-hidden rounded-3xl border border-white/10 bg-white/5 p-6 shadow-card backdrop-blur-md">
              <div className="flex items-center justify-between">
                <span className="text-xs font-semibold uppercase tracking-widest text-white/60">
                  Merchant Center
                </span>
                <span className="inline-flex items-center gap-1 rounded-full bg-brand-500/20 px-2.5 py-1 text-[10px] font-semibold text-brand-200">
                  <span className="h-1.5 w-1.5 rounded-full bg-brand-300" />
                  Live
                </span>
              </div>
              <div className="mt-5">
                <p className="text-xs text-white/50">Monthly volume</p>
                <p className="mt-1 font-display text-4xl font-bold tracking-tight">
                  $1.2B+
                </p>
              </div>
              <div className="mt-6 grid grid-cols-3 gap-3">
                {["Visa", "MC", "Amex", "ACH", "Pix", "SEPA"].map((x) => (
                  <div
                    key={x}
                    className="rounded-xl border border-white/10 bg-white/5 px-2 py-3 text-center text-xs font-semibold text-white/80"
                  >
                    {x}
                  </div>
                ))}
              </div>
              <div className="mt-6 space-y-3">
                {[
                  { label: "Successful txns", v: "98.7%" },
                  { label: "Chargebacks", v: "0.42%" },
                  { label: "Avg. latency", v: "148ms" },
                ].map((row) => (
                  <div
                    key={row.label}
                    className="flex items-center justify-between rounded-lg bg-white/5 px-3 py-2"
                  >
                    <span className="text-xs text-white/60">{row.label}</span>
                    <span className="text-sm font-semibold">{row.v}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function HeroStat({ value, label }) {
  return (
    <div>
      <div className="font-display text-2xl font-bold text-white">{value}</div>
      <div className="mt-1 text-xs uppercase tracking-wider text-white/50">
        {label}
      </div>
    </div>
  );
}

function TrustBar() {
  const items = ["VISA", "MASTERCARD", "AMEX", "DISCOVER", "JCB", "UNIONPAY"];
  return (
    <div className="border-b border-ink-900/5 bg-white">
      <div className="container-page py-8">
        <p className="text-center text-xs font-semibold uppercase tracking-[0.24em] text-ink-400">
          Powering payments with the world's leading networks
        </p>
        <div className="marquee mt-6 flex flex-wrap items-center justify-center gap-x-10 gap-y-4 sm:gap-x-16">
          {items.map((x) => (
            <span
              key={x}
              className="font-display text-lg font-bold tracking-[0.25em] text-ink-900/40"
            >
              {x}
            </span>
          ))}
        </div>
      </div>
    </div>
  );
}

function About() {
  return (
    <section id="about" className="section bg-white">
      <div className="container-page grid items-center gap-14 lg:grid-cols-2">
        <div>
          <span className="eyebrow">About Us</span>
          <h2 className="section-title mt-3">
            Pioneering Secure Payment Solutions for High-Risk Merchants
          </h2>
          <p className="section-lead">
            We specialize in providing cutting-edge payment solutions tailored specifically for high-risk merchants. We understand the unique challenges and complexities faced by businesses operating in high-risk industries and have developed innovative technologies and robust strategies to ensure secure transactions and reliable payment processing.
          </p>
          <p className="mt-5 max-w-2xl text-base leading-relaxed text-ink-500">
            Our team of experts is dedicated to staying ahead of the curve in the ever-evolving payment processing landscape. By leveraging the latest advancements in encryption, fraud detection, and risk management techniques, we offer our clients peace of mind, knowing their transactions are protected against potential threats.
          </p>
          <Link to="/about-us" className="btn-outline mt-8">
            Learn More
            <ArrowRight className="h-4 w-4" />
          </Link>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <img
            src={IMG.about1}
            alt="Secure payment technology"
            className="col-span-1 h-60 w-full rounded-2xl object-cover shadow-card sm:h-72"
            loading="lazy"
          />
          <img
            src={IMG.about2}
            alt="Global fintech"
            className="col-span-1 h-60 w-full translate-y-8 rounded-2xl object-cover shadow-card sm:h-72"
            loading="lazy"
          />
          <img
            src={IMG.about3}
            alt="Risk management"
            className="col-span-2 h-56 w-full rounded-2xl object-cover shadow-card"
            loading="lazy"
          />
        </div>
      </div>
    </section>
  );
}

function Payment() {
  return (
    <section className="section bg-brand-50/60">
      <div className="container-page grid gap-12 lg:grid-cols-12">
        <div className="lg:col-span-5">
          <span className="eyebrow">Payment</span>
          <h2 className="section-title mt-3">Making payments available for all</h2>
          <p className="section-lead">
            SnapPay is on a mission to create the most accessible financial services ecosystem in history. To do so, we are building the global operating system for fintech, starting with payments.
          </p>
        </div>
        <div className="grid gap-8 lg:col-span-7 sm:grid-cols-2">
          <div className="card">
            <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-brand-100 text-brand-700">
              <Globe2 className="h-5 w-5" />
            </div>
            <h3 className="mt-5 font-display text-xl font-bold text-ink-900">
              Our Mission
            </h3>
            <p className="mt-3 text-sm leading-relaxed text-ink-500">
              Moving money is a fundamental human activity and critical to the health of our global economy. Since our founding in 2015, SnapPay has been rewriting the financial services industry one API at a time to lower barriers to entry, drive competition, and make the financial system more equitable and accessible for people around the world.
            </p>
          </div>
          <div className="card">
            <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-brand-100 text-brand-700">
              <ShieldCheck className="h-5 w-5" />
            </div>
            <h3 className="mt-5 font-display text-xl font-bold text-ink-900">
              Our Beliefs
            </h3>
            <p className="mt-3 text-sm leading-relaxed text-ink-500">
              We believe every business should have access to the best and most innovative payment technology — and that they should earn the bulk of the economic benefit. To accelerate access to payments and fintech for the greater good, we need to remove the technical, compliance, regulatory, and global barriers to entry.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

const SOLUTIONS = [
  {
    eyebrow: "Merchant Accounts",
    title: "Low to High-Risk Industries",
    body: "We have been building close relationships with over twenty acquiring partners across the globe.",
    icon: CreditCard,
  },
  {
    eyebrow: "Alternative Payment Methods",
    title: "Increase Your Conversion Rate",
    body: "ACH, virtual prepaid cards, Pix, SEPA, iDeal, GiroPay, AliPay, WeChat and many more.",
    icon: Wallet,
  },
  {
    eyebrow: "Chargeback Mitigation & Fraud Protection",
    title: "Optimize Your Customer Journey",
    body: "Custom features and risk & fraud mitigation tools to decrease overall chargebacks.",
    icon: ShieldCheck,
  },
  {
    eyebrow: "Payment Compliance",
    title: "Card Schemes Rules & Regulations",
    body: "Weekly updates and strategies to remain in full compliance.",
    icon: Scale,
  },
  {
    eyebrow: "Payments & Risk Consultation",
    title: "Improve Your Internal Payment Operations",
    body: "Innovative ideas when it comes to a payment and risk internal structure.",
    icon: LineChart,
  },
  {
    eyebrow: "Payouts",
    title: "International Business Partner Payout Solutions",
    body: "To grow your business internationally through affiliates and other business partners.",
    icon: Globe2,
  },
];

function Solutions() {
  return (
    <section id="solutions" className="section bg-white">
      <div className="container-page">
        <div className="max-w-3xl">
          <span className="eyebrow">Solutions</span>
          <h2 className="section-title mt-3">
            We pair online merchants with the most reputable acquiring banks in the market.
          </h2>
        </div>

        <div className="mt-14 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {SOLUTIONS.map(({ eyebrow, title, body, icon: Icon }) => (
            <div key={title} className="card group">
              <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-gradient-to-br from-brand-500 to-brand-700 text-white shadow-soft">
                <Icon className="h-6 w-6" />
              </div>
              <p className="mt-6 text-[10px] font-bold uppercase tracking-[0.2em] text-brand-600">
                {eyebrow}
              </p>
              <h3 className="mt-2 font-display text-lg font-bold leading-snug text-ink-900">
                {title}
              </h3>
              <p className="mt-3 text-sm leading-relaxed text-ink-500">{body}</p>
              <div className="mt-6 inline-flex items-center gap-1.5 text-sm font-semibold text-brand-600 transition group-hover:gap-2.5">
                Learn more
                <ArrowRight className="h-4 w-4" />
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function Leading() {
  return (
    <section className="section bg-ink-900 text-white">
      <div className="container-page grid items-center gap-14 lg:grid-cols-2">
        <div className="relative">
          <div className="absolute -inset-4 rounded-[2.2rem] bg-gradient-to-br from-brand-500/30 to-transparent blur-2xl" />
          <img
            src={IMG.leading}
            alt="Leading high-risk merchant services"
            className="relative h-[440px] w-full rounded-3xl object-cover shadow-card"
            loading="lazy"
          />
          <div className="absolute -bottom-6 left-6 flex items-center gap-3 rounded-2xl border border-white/10 bg-ink-900/80 px-4 py-3 shadow-card backdrop-blur">
            <div className="flex h-9 w-9 items-center justify-center rounded-full bg-brand-500/20 text-brand-300">
              <CheckCircle2 className="h-5 w-5" />
            </div>
            <div className="pr-2">
              <div className="text-sm font-bold">99.9% Uptime</div>
              <div className="text-[11px] uppercase tracking-wider text-white/50">
                PCI DSS Level 1
              </div>
            </div>
          </div>
        </div>

        <div>
          <span className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.22em] text-brand-300">
            Enterprise-grade
          </span>
          <h2 className="mt-4 font-display text-3xl font-bold leading-tight sm:text-4xl lg:text-5xl">
            Leading high-risk merchant services providers
          </h2>

          <div className="mt-8 space-y-6">
            <Feature
              title="Integration with multiple acquiring banks"
              body="The acquirer-agnostic gateway allows the merchant to partner with any bank and to have multiple Merchant IDs (MIDs) within the same integration."
              icon={<Globe2 className="h-5 w-5" />}
            />
            <Feature
              title="Friendly Interface"
              body="Receive early warnings of any chargebacks raised with our chargeback defender tool so you can turn them into refunds and reduce costs."
              icon={<LineChart className="h-5 w-5" />}
            />
            <Feature
              title="Extra security controls"
              body="Powerful and flexible card verification and security tools allow you to confirm details like security cards and customer addresses."
              icon={<Lock className="h-5 w-5" />}
            />
          </div>

          <div className="mt-8">
            <Link to="/contact-us" className="btn-primary">
              Get Started Now
              <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}

function Feature({ title, body, icon }) {
  return (
    <div className="flex gap-4">
      <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-brand-500/15 text-brand-300">
        {icon}
      </div>
      <div>
        <h4 className="font-display text-lg font-semibold text-white">{title}</h4>
        <p className="mt-1.5 text-sm leading-relaxed text-white/60">{body}</p>
      </div>
    </div>
  );
}

const STATS = [
  { n: 100, suffix: "+", label: "Ways to Pay" },
  { n: 24, suffix: "/7", label: "Tech Support" },
  { n: 150, suffix: "+", label: "Processing Currencies" },
  { n: 20, suffix: "+", label: "Offices Globally" },
  { n: 50, suffix: "+", label: "Settlement Currencies" },
];

function WhyChooseUs() {
  return (
    <section id="why-us" className="section bg-brand-50/50">
      <div className="container-page">
        <div className="text-center">
          <span className="eyebrow">Why Choose Us?</span>
          <h2 className="section-title mx-auto mt-3 max-w-3xl">
            Built for merchants who can't afford to compromise
          </h2>
        </div>

        <div className="mt-14 grid grid-cols-2 gap-5 md:grid-cols-5">
          {STATS.map((s) => (
            <div
              key={s.label}
              className="rounded-2xl border border-ink-900/5 bg-white p-6 text-center shadow-card"
            >
              <div className="font-display text-4xl font-bold text-ink-900 sm:text-5xl">
                <Counter end={s.n} suffix={s.suffix} />
              </div>
              <div className="mt-2 text-xs font-semibold uppercase tracking-widest text-ink-500">
                {s.label}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function Empower() {
  return (
    <section className="section bg-white">
      <div className="container-page grid items-center gap-14 lg:grid-cols-2">
        <div className="order-2 lg:order-1">
          <span className="eyebrow">Limited Time Offer</span>
          <h3 className="mt-3 text-sm font-semibold uppercase tracking-widest text-ink-400">
            We Specialize in High Risk Business
          </h3>
          <h2 className="section-title mt-3">
            Empowering High-Risk Merchants with Secure Payment Solutions
          </h2>
          <p className="section-lead">
            Our mission at SnapPay is to empower high-risk merchants by providing secure payment solutions that enable them to operate confidently in today's dynamic marketplace. We understand the challenges and obstacles faced by businesses in high-risk industries and are committed to helping them navigate these challenges with ease.
          </p>

          <div className="mt-8 space-y-5">
            <Bullet title="Instantly onboard merchants">
              We handle the KYC process so your merchants can onboard quickly and start accepting payments.
            </Bullet>
            <Bullet title="Do more with digital servicing">
              Merchants can manage onboarding, transactions, reporting, chargebacks, user access and more with our complete Merchant Center.
            </Bullet>
            <Bullet title="Grow your payments with ease">
              Our Partner Center has powerful capabilities offering sophisticated reporting, analytics, support and customization.
            </Bullet>
          </div>

          <Link to="/contact-us" className="btn-dark mt-9">
            Contact Us
            <ArrowRight className="h-4 w-4" />
          </Link>
        </div>

        <div className="relative order-1 lg:order-2">
          <img
            src={IMG.empower}
            alt="Empowering merchants"
            className="h-[460px] w-full rounded-3xl object-cover shadow-card"
            loading="lazy"
          />
          <div className="absolute -left-4 bottom-6 rounded-2xl border border-ink-900/5 bg-white px-5 py-4 shadow-card sm:left-6">
            <div className="text-[11px] font-semibold uppercase tracking-widest text-ink-400">
              Monthly Volume
            </div>
            <div className="font-display text-2xl font-bold text-ink-900 sm:text-3xl">
              $1.2B+
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function Bullet({ title, children }) {
  return (
    <div className="flex gap-4">
      <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-brand-100 text-brand-700">
        <CheckCircle2 className="h-5 w-5" />
      </div>
      <div>
        <h4 className="font-display text-lg font-semibold text-ink-900">{title}</h4>
        <p className="mt-1.5 text-sm leading-relaxed text-ink-500">{children}</p>
      </div>
    </div>
  );
}

const TESTIMONIALS = [
  {
    quote:
      "Our experience with SnapPay has been nothing short of outstanding. Their platform offers seamless integration, robust security features, and lightning-fast transaction processing. With responsive customer support available round the clock, we have peace of mind knowing that our payments are in safe hands.",
    name: "Niyi Yusuf",
    role: "Operations Lead",
    img: IMG.t1,
  },
  {
    quote:
      "SnapPay has truly transformed the way we conduct transactions. Their intuitive interface, coupled with a wide range of payment options, has significantly improved our customer experience. The platform's advanced analytics also provide valuable insights into our sales performance, helping us make data-driven decisions.",
    name: "Grace Wallace",
    role: "Finance Director",
    img: IMG.t2,
  },
  {
    quote:
      "We switched to SnapPay seeking a solution that could streamline our payment processes, and we were not disappointed. The platform's user-friendly dashboard makes managing transactions a breeze, while its robust fraud prevention measures give us peace of mind.",
    name: "Ufoma Joseph",
    role: "Head of Payments",
    img: IMG.t3,
  },
];

function Testimonials() {
  return (
    <section className="section bg-ink-900 text-white">
      <div className="container-page">
        <div className="text-center">
          <span className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.22em] text-brand-300">
            Testimonials
          </span>
          <h2 className="mx-auto mt-4 max-w-2xl font-display text-3xl font-bold leading-tight sm:text-4xl lg:text-5xl">
            What Our Clients Say About Us
          </h2>
        </div>

        <div className="mt-14 grid gap-6 lg:grid-cols-3">
          {TESTIMONIALS.map((t) => (
            <div
              key={t.name}
              className="relative flex h-full flex-col rounded-2xl border border-white/10 bg-white/5 p-8 backdrop-blur-sm"
            >
              <Quote className="h-8 w-8 text-brand-300" />
              <p className="mt-5 text-sm leading-relaxed text-white/80">{t.quote}</p>
              <div className="mt-8 flex items-center gap-3 border-t border-white/10 pt-5">
                <img
                  src={t.img}
                  alt={t.name}
                  className="h-11 w-11 rounded-full object-cover ring-2 ring-white/10"
                  loading="lazy"
                />
                <div>
                  <div className="text-sm font-semibold">{t.name}</div>
                  <div className="text-xs text-white/50">{t.role}</div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

const POSTS = [
  {
    tag: "Chargebacks",
    date: "April 14, 2024",
    title: "How to reduce chargebacks for high-risk merchants",
    img: IMG.blog1,
  },
  {
    tag: "Compliance",
    date: "April 02, 2024",
    title: "Understanding card scheme rules in 2024",
    img: IMG.blog2,
  },
  {
    tag: "Payments",
    date: "March 21, 2024",
    title: "Alternative payment methods that boost conversion",
    img: IMG.blog3,
  },
];

function Blog() {
  return (
    <section id="blog" className="section bg-white">
      <div className="container-page">
        <div className="flex flex-wrap items-end justify-between gap-6">
          <div className="max-w-2xl">
            <span className="eyebrow">Blog</span>
            <h2 className="section-title mt-3">Latest News &amp; Blog</h2>
            <p className="section-lead">
              Insights, updates and best practices on high-risk payment processing, fraud prevention, and merchant services.
            </p>
          </div>
        </div>

        <div className="mt-12 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {POSTS.map((p) => (
            <article
              key={p.title}
              className="group overflow-hidden rounded-2xl border border-ink-900/5 bg-white shadow-card transition hover:-translate-y-1"
            >
              <div className="relative aspect-[16/10] overflow-hidden">
                <img
                  src={p.img}
                  alt={p.title}
                  className="h-full w-full object-cover transition duration-500 group-hover:scale-105"
                  loading="lazy"
                />
                <span className="absolute left-4 top-4 rounded-full bg-white/95 px-3 py-1 text-[10px] font-bold uppercase tracking-wider text-brand-700">
                  {p.tag}
                </span>
              </div>
              <div className="p-6">
                <div className="text-xs font-semibold uppercase tracking-widest text-ink-400">
                  {p.date} &middot; SnapPay Team
                </div>
                <h3 className="mt-3 font-display text-lg font-bold leading-snug text-ink-900">
                  {p.title}
                </h3>
                <div className="mt-5 inline-flex items-center gap-1.5 text-sm font-semibold text-brand-600 transition group-hover:gap-2.5">
                  Read More
                  <ArrowRight className="h-4 w-4" />
                </div>
              </div>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}

function CTA() {
  return (
    <section className="bg-gradient-to-br from-brand-700 via-brand-600 to-brand-500 text-white">
      <div className="container-page grid items-center gap-8 py-16 md:grid-cols-[1.3fr_auto] md:py-20">
        <div>
          <h2 className="font-display text-2xl font-bold leading-tight sm:text-3xl lg:text-4xl">
            Ready to accept payments the smart way?
          </h2>
          <p className="mt-3 max-w-2xl text-sm text-white/80 sm:text-base">
            Talk to our payments experts today and get a custom processing plan that fits your industry — we specialize in high-risk.
          </p>
        </div>
        <div className="flex flex-wrap gap-3">
          <Link to="/contact-us" className="btn-dark">
            Contact Sales
            <ArrowRight className="h-4 w-4" />
          </Link>
          <Link to="/about-us" className="btn-ghost">
            Learn More
          </Link>
        </div>
      </div>
    </section>
  );
}
